import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { useMemo } from 'react';
import BigNumber from 'bignumber.js';

import { AshswapQueryKeys } from './useGetAshswapTokens';
import { AshswapAggregator } from './AshswapAggregator';

const parseAmount = (amount: string, decimals: number): string => {
  return BigNumber(amount).multipliedBy(BigNumber(10).pow(decimals)).toFixed(0);
};

interface WalletTokenSchema {
  identifier: string;
  decimals: number;
  ticker?: string;
  name: string;
  balance?: string;
}

export const useGetAshswapAggregation = (
  from?: WalletTokenSchema,
  to?: string,
  amount?: string,
  slippage: number = 100
) => {
  const fromDebounced = useDebounce(from, 170);
  const toDebounced = useDebounce(to, 170);
  const amountDebounced = useDebounce(amount, 170);
  const slippageDebounced = useDebounce(slippage, 170);

  const amountNominated = useMemo(() => {
    try {
      return parseAmount(amountDebounced ?? '0', fromDebounced?.decimals ?? 1);
    } catch (e) {
      return '0';
    }
  }, [fromDebounced, amountDebounced]);

  return useQuery({
    queryKey: AshswapQueryKeys.aggregation(
      fromDebounced?.identifier ?? '',
      toDebounced ?? '',
      amountNominated,
      slippageDebounced
    ),
    queryFn: async () => {
      if (
        !fromDebounced ||
        !toDebounced ||
        !amountDebounced ||
        BigNumber(amountDebounced).lte(0) ||
        BigNumber(amountDebounced).isNaN()
      ) {
        return {
          sorResponse: undefined,
          getInteraction: undefined,
        };
      }
      try {
        const { sorResponse, getInteraction } =
          await AshswapAggregator.aggregate(
            fromDebounced?.identifier ?? '',
            toDebounced ?? '',
            amountNominated,
            slippageDebounced
          );
        return { sorResponse, getInteraction };
      } catch (e) {
        return {
          sorResponse: undefined,
          getInteraction: undefined,
        };
      }
    },
    refetchInterval: 1000 * 10,
  });
};